// Newest first on /jobs is a timeline grouped by the day jobs were posted.
// Within each day Twine jobs come first, then jobs from other sites. Twine
// jobs arrive in batches, so a day's external jobs are only shown once every
// Twine job for that day has loaded (the next batch may still add some).

/** Time zone for the first page load and search engines. */
export const SSR_TIME_ZONE = 'UTC';

/** External jobs expire 30 days after posting, so nothing older is shown. */
export const EXTERNAL_MAX_DAYS_BACK = 30;

/** The API returns at most this many days per request. */
export const EXTERNAL_MAX_DAYS_PER_REQUEST = 31;

/** Extra days of external jobs revealed per load once Twine jobs run out. */
export const EXTERNAL_DAYS_PER_LOAD = 7;

/** Once Twine jobs run out, this many days before the last one show at once. */
export const EXTERNAL_INITIAL_DAYS_BACK = 6;

const dayFormatters = {};

/**
 * Calendar day ('YYYY-MM-DD') of a date in a time zone.
 *
 * @param {string|number|Date} date
 * @param {string} timeZone
 * @returns {string|null}
 */
export const dayKey = (date, timeZone = SSR_TIME_ZONE) => {
  const value = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(value.getTime())) {
    return null;
  }
  if (!dayFormatters[timeZone]) {
    // en-CA formats as YYYY-MM-DD.
    dayFormatters[timeZone] = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }
  return dayFormatters[timeZone].format(value);
};

/**
 * @param {string} key 'YYYY-MM-DD'
 * @param {number} days
 * @returns {string}
 */
export const addDays = (key, days) => {
  const date = new Date(`${key}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

/**
 * Visitor's time zone, or the SSR zone when it can't be read.
 *
 * @returns {string}
 */
export const getVisitorTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || SSR_TIME_ZONE;
  } catch (e) {
    return SSR_TIME_ZONE;
  }
};

/** Posting date the job card shows ("Posted today"). */
const briefPostedAt = (brief) => brief.approved_at || brief.timestamp;

/**
 * Boosted jobs are listed before everything else by the API, so they sit in
 * a featured block above the timeline rather than under the wrong day.
 *
 * @param {Array} briefs In API order.
 * @returns {{featured: Array, rest: Array}}
 */
export const splitFeatured = (briefs) => {
  let count = 0;
  while (count < briefs.length && briefs[count].boosted_expires_at) {
    count += 1;
  }
  return { featured: briefs.slice(0, count), rest: briefs.slice(count) };
};

/**
 * Oldest day whose external jobs can be shown: the day after the last loaded
 * Twine job while more Twine jobs may follow, otherwise the external floor.
 *
 * @param {object} args
 * @param {Array} args.briefs Non-featured Twine jobs, newest first.
 * @param {boolean} args.moreToLoad More Twine batches to come.
 * @param {number} args.externalDaysBack Days revealed past the last Twine day.
 * @param {string} args.today
 * @param {string} args.timeZone
 * @returns {string|null} 'YYYY-MM-DD', or null when no day is complete yet.
 */
export const getExternalFloor = ({
  briefs,
  moreToLoad,
  externalDaysBack = 0,
  today,
  timeZone,
}) => {
  const oldest = addDays(today, -EXTERNAL_MAX_DAYS_BACK);
  const lastBrief = briefs.length ? briefs[briefs.length - 1] : null;
  const lastDay = lastBrief ? dayKey(briefPostedAt(lastBrief), timeZone) : null;

  if (moreToLoad) {
    if (!lastDay) {
      return null;
    }
    const floor = addDays(lastDay < today ? lastDay : today, 1);
    if (floor > today) {
      return null;
    }
    return floor < oldest ? oldest : floor;
  }

  const start = lastDay && lastDay < today ? lastDay : today;
  const floor = addDays(
    start,
    -(EXTERNAL_INITIAL_DAYS_BACK + externalDaysBack)
  );
  return floor < oldest ? oldest : floor;
};

/**
 * Whether revealing more days would still find external jobs to show.
 *
 * @param {string|null} floor
 * @param {string} today
 * @returns {boolean}
 */
export const hasMoreExternalDays = (floor, today) =>
  Boolean(floor) && floor > addDays(today, -EXTERNAL_MAX_DAYS_BACK);

/**
 * Days from `floor` to `today` that haven't been requested yet, as one
 * request range (newest-first callers keep the gap contiguous).
 *
 * @param {string|null} floor
 * @param {string} today
 * @param {object} fetchedDays date => true
 * @returns {{from: string, to: string}|null}
 */
export const getMissingExternalRange = (floor, today, fetchedDays = {}) => {
  if (!floor || floor > today) {
    return null;
  }
  let to = null;
  let from = null;
  for (let day = today; day >= floor; day = addDays(day, -1)) {
    if (!fetchedDays[day]) {
      if (!to) {
        to = day;
      }
      from = day;
      if (
        Math.abs(daysBetween(from, to)) + 1 >=
        EXTERNAL_MAX_DAYS_PER_REQUEST
      ) {
        break;
      }
    } else if (to) {
      break;
    }
  }
  return to ? { from, to } : null;
};

const daysBetween = (a, b) =>
  Math.round(
    (new Date(`${a}T00:00:00Z`) - new Date(`${b}T00:00:00Z`)) / 86400000
  );

/**
 * Group Twine jobs and external days into the timeline.
 *
 * @param {object} args
 * @param {Array} args.briefs Twine jobs in API order.
 * @param {object} args.externalDays date => {total, jobs}
 * @param {boolean} args.moreToLoad
 * @param {number} args.externalDaysBack
 * @param {string} args.today
 * @param {string} args.timeZone
 * @returns {{featured: Array, days: Array<{date: string, twine: Array, external: {total: number, jobs: Array}|null}>, floor: string|null}}
 */
export const buildJobsTimeline = ({
  briefs,
  externalDays = {},
  moreToLoad,
  externalDaysBack = 0,
  today,
  timeZone,
}) => {
  const { featured, rest } = splitFeatured(briefs);
  const floor = getExternalFloor({
    briefs: rest,
    moreToLoad,
    externalDaysBack,
    today,
    timeZone,
  });

  const days = [];
  const byDate = {};
  const dayFor = (date) => {
    if (!byDate[date]) {
      byDate[date] = { date, twine: [], external: null };
      days.push(byDate[date]);
    }
    return byDate[date];
  };

  rest.forEach((brief) => {
    let date = dayKey(briefPostedAt(brief), timeZone) || today;
    if (date > today) {
      date = today;
    }
    // The API orders by id, so a job approved late can look newer than the
    // day before it. Keep it in the current day so days never repeat.
    const current = days[days.length - 1];
    if (current && date > current.date) {
      date = current.date;
    }
    dayFor(date).twine.push(brief);
  });

  if (floor) {
    Object.keys(externalDays).forEach((date) => {
      const day = externalDays[date];
      if (date >= floor && date <= today && day && day.total > 0) {
        dayFor(date).external = day;
      }
    });
  }

  days.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

  return { featured, days, floor };
};

const weekdayFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  timeZone: 'UTC',
});

const weekday = (key) => weekdayFormatter.format(new Date(`${key}T00:00:00Z`));

/**
 * Day heading copy: "Posted today" / "Yesterday" with the full date beside
 * them, the weekday with the date beside it for the rest of the past week,
 * then just the date (a weekday alone is ambiguous after seven days).
 *
 * @param {string} date
 * @param {string} today
 * @returns {{title: string, subtitle: string|null, relative: 'today'|'yesterday'|null}}
 */
export const formatDayHeading = (date, today) => {
  if (date === today) {
    return {
      title: 'Posted today',
      subtitle: `${weekday(date)}, ${date}`,
      relative: 'today',
    };
  }
  if (date === addDays(today, -1)) {
    return {
      title: 'Yesterday',
      subtitle: `${weekday(date)}, ${date}`,
      relative: 'yesterday',
    };
  }
  if (date >= addDays(today, -6)) {
    return { title: weekday(date), subtitle: date, relative: null };
  }
  return { title: date, subtitle: null, relative: null };
};

/**
 * "Show all [N] jobs from other sites posted today".
 *
 * @param {number} total
 * @param {'today'|'yesterday'|null} relative
 * @returns {string}
 */
export const showAllExternalLabel = (total, relative) => {
  const when =
    relative === 'today'
      ? 'today'
      : relative === 'yesterday'
      ? 'yesterday'
      : 'that day';
  return `Show all ${total} jobs from other sites posted ${when}`;
};
